import { useState, useEffect, useCallback, useMemo } from 'react';
import {
    useAddMainCategoriesMutation,
    useAddSecondCategoriesMutation,
    useAddSubCategoriesMutation,
    useAddSubPartCategoriesMutation,
    useAddFailureModesCategoryMutation,
    useUpdateMainCategoriesMutation,
    useUpdateSecondCategoriesMutation,
    useUpdateSubCategoriesMutation,
    useUpdateSubPartCategoriesMutation,
    useUpdateFailureModesCategoryMutation,
    useGetTranslationListQuery,
    useDeleteFaultCodesMutation
} from '../../../../api';
import { CATEGORY_LEVELS } from '../components/CategoryTable';
import { useLanguage } from '../../../../contexts/LanguageContext';

const createInitialTranslations = (supportedLanguages = []) => {
    const translations = {};

    if (supportedLanguages && Array.isArray(supportedLanguages)) {
        supportedLanguages.forEach(lang => {
            const validLanguageId = lang.id;

            if (lang && lang.code) {
                translations[lang.code] = {
                    id: null,
                    isActive: true,
                    languageId: validLanguageId,
                    text: '',
                    description: '',
                    seoUrl: '',
                    metaKeywords: '',
                    metaDescription: '',
                };
            }
        });
    }
    return translations;
};
const getBaseInitialFormData = (supportedLanguages) => ({
    id: null,
    translations: createInitialTranslations(supportedLanguages),
    mainCategory: null,
    secondaryCategory: null,
    subCategory: null,
    isActive: 1,
    translationId: null,
});

export const useManageCategoryDialog = (categoryLevel, supportedLanguages, onSuccessCallback) => {
    const { getCurrentLanguageId } = useLanguage();

    const [dialogVisible, setDialogVisible] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [formData, setFormData] = useState(() =>
        getBaseInitialFormData(supportedLanguages)
    );
    const [submitted, setSubmitted] = useState(false);

    const [addMainCategoryFn, { isLoading: isAddingMain, error: addMainError }] = useAddMainCategoriesMutation();
    const [updateMainCategoryFn, { isLoading: isUpdatingMain, error: updateMainError }] = useUpdateMainCategoriesMutation();
    const [addSecondCategoryFn, { isLoading: isAddingSecond, error: addSecondError }] = useAddSecondCategoriesMutation();
    const [updateSecondCategoryFn, { isLoading: isUpdatingSecond, error: updateSecondError }] = useUpdateSecondCategoriesMutation();
    const [addSubCategoryFn, { isLoading: isAddingSub, error: addSubError }] = useAddSubCategoriesMutation();
    const [updateSubCategoryFn, { isLoading: isUpdatingSub, error: updateSubError }] = useUpdateSubCategoriesMutation();
    const [addSubPartCategoryFn, { isLoading: isAddingSubPart, error: addSubPartError }] = useAddSubPartCategoriesMutation();
    const [updateSubPartCategoryFn, { isLoading: isUpdatingSubPart, error: updateSubPartError }] = useUpdateSubPartCategoriesMutation();
    const [addFailureModesFn, { isLoading: isAddingFailureMode, error: addFailureModeError }] = useAddFailureModesCategoryMutation();
    const [updateFailureModesFn, { isLoading: isUpdatingFailureMode, error: updateFailureModeError }] = useUpdateFailureModesCategoryMutation();

    const [deleteFaultCodesFn, { isLoading: isDeleting, error: deleteError }] = useDeleteFaultCodesMutation();

    const {
        data: fetchedTranslationsResponse,
        isLoading: isLoadingTranslations,
        isSuccess: isTranslationsSuccess,
        isError: isTranslationsError,
        error: translationsError
    } = useGetTranslationListQuery(
        { translationId: formData.translationId, RowCount: 6 },
        { skip: !isEditMode || !formData.translationId || !dialogVisible }
    );

    useEffect(() => {
        if (supportedLanguages && supportedLanguages.length > 0) {
            setFormData(prev => {
                const newInitialTranslations = createInitialTranslations(supportedLanguages); // Removed getCurrentLanguageId()
                const mergedTranslations = {};

                for (const langCode in newInitialTranslations) {
                    if (Object.prototype.hasOwnProperty.call(newInitialTranslations, langCode)) {
                        const previousLangData = prev.translations?.[langCode];
                        if (previousLangData) {
                            mergedTranslations[langCode] = {
                                ...previousLangData,
                                languageId: newInitialTranslations[langCode].languageId,
                            };
                        } else {
                            mergedTranslations[langCode] = newInitialTranslations[langCode];
                        }
                    }
                }
                return {
                    ...prev,
                    translations: mergedTranslations,
                    isActive: prev.id ? prev.isActive : 1
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                translations: createInitialTranslations([]),
                isActive: prev.id ? prev.isActive : 1
            }));
        }
    }, [supportedLanguages]);

    useEffect(() => {
        if (isEditMode && isTranslationsSuccess && fetchedTranslationsResponse?.data && dialogVisible) {
            setFormData(prev => {
                const newTranslationsState = { ...prev.translations };
                const fetchedTranslationsMap = new Map();

                fetchedTranslationsResponse.data.forEach(fetchedTranslationItem => {
                    fetchedTranslationsMap.set(fetchedTranslationItem.languageId, fetchedTranslationItem);
                });

                if (supportedLanguages && Array.isArray(supportedLanguages)) {
                    supportedLanguages.forEach(supportedLang => {
                        const langCode = supportedLang.code.toLowerCase();

                        if (langCode && newTranslationsState[langCode]) {
                            const basePrevTranslationForLangCode = newTranslationsState[langCode];
                            const fetchedApiTranslation = fetchedTranslationsMap.get(supportedLang.id);

                            if (fetchedApiTranslation) {
                                newTranslationsState[langCode] = {
                                    ...basePrevTranslationForLangCode,
                                    id: fetchedApiTranslation.id ?? basePrevTranslationForLangCode.id ?? null,
                                    languageId: supportedLang.id,
                                    text: fetchedApiTranslation.text ?? basePrevTranslationForLangCode.text ?? '',
                                    description: fetchedApiTranslation.description ?? basePrevTranslationForLangCode.description ?? '',
                                    seoUrl: fetchedApiTranslation.seoUrl ?? basePrevTranslationForLangCode.seoUrl ?? '',
                                    metaKeywords: fetchedApiTranslation.metaKeywords ?? basePrevTranslationForLangCode.metaKeywords ?? '',
                                    metaDescription: fetchedApiTranslation.metaDescription ?? basePrevTranslationForLangCode.metaDescription ?? '',
                                };
                            } else {
                                newTranslationsState[langCode] = {
                                    ...basePrevTranslationForLangCode,
                                    languageId: supportedLang.id,
                                    text: '',
                                    description: '',
                                    seoUrl: '',
                                    metaKeywords: '',
                                    metaDescription: '',
                                };
                            }
                        }
                    });
                }
                return { ...prev, translations: newTranslationsState };
            });
        } else if (!isEditMode && dialogVisible) {
            setFormData(getBaseInitialFormData(supportedLanguages));
        }
    }, [isEditMode, isTranslationsSuccess, fetchedTranslationsResponse, supportedLanguages, dialogVisible]); // Removed getCurrentLanguageId dependency
    const openDialog = useCallback((initialData = {}, editMode = false) => {
        setIsEditMode(editMode);
        setSubmitted(false);

        let baseForm = getBaseInitialFormData(supportedLanguages);
        let newFormData = {
            ...baseForm,
            ...initialData,
            categoryLevel: categoryLevel,
            mainCategory: initialData.mainCategory || null,
            secondaryCategory: initialData.secondaryCategory || null,
            subCategory: initialData.subCategory || null,
            isActive: initialData.isActive !== undefined ? initialData.isActive : (initialData.status !== undefined ? (initialData.status ? 1 : 2) : baseForm.isActive),
        };

        if (editMode) {
            newFormData.translationId = initialData.translationId || null;
            newFormData.id = initialData.id || null;

            if (initialData.translations) {
                const mergedTranslations = { ...baseForm.translations };

                supportedLanguages.forEach(supportedLang => {
                    const langCode = supportedLang.code.toLowerCase();
                    const initialLangData = initialData.translations[langCode] ||
                        initialData.translations[langCode.toUpperCase()] ||
                        (Array.isArray(initialData.translations) ?
                            initialData.translations.find(t => t.languageCode?.toLowerCase() === langCode) : null);

                    if (initialLangData) {
                        mergedTranslations[langCode] = {
                            ...mergedTranslations[langCode],
                            ...initialLangData,
                            languageId: supportedLang.id,
                            text: initialLangData.text || initialLangData.name || '',
                        };
                    }
                });
                newFormData.translations = mergedTranslations;
            }
        } else {
            newFormData.translationId = null;
            newFormData.id = null;
            newFormData.isActive = 1;
        }
        setFormData(newFormData);
        setDialogVisible(true);
    }, [categoryLevel, supportedLanguages]);


    const hideDialog = useCallback(() => {
        setDialogVisible(false);
        setSubmitted(false);
        setFormData(getBaseInitialFormData(supportedLanguages));
    }, [supportedLanguages]);

    const handleInputChange = useCallback((eventLikeObject, fieldPathOrName) => {
        const nameAttribute = eventLikeObject.target?.name;
        const resolvedName = nameAttribute || fieldPathOrName;
        let value;

        if (eventLikeObject.target && 'value' in eventLikeObject.target) {
            value = eventLikeObject.target.type === 'checkbox' ? eventLikeObject.target.checked : eventLikeObject.target.value;
        } else {
            value = eventLikeObject;
        }

        setFormData(prev => {
            const newState = JSON.parse(JSON.stringify(prev));

            if (resolvedName.startsWith('translations.')) {
                const parts = resolvedName.split('.');
                const langCode = parts[1];
                const fieldName = parts[2];

                if (!newState.translations) {
                    newState.translations = {};
                }

                const language = supportedLanguages.find(l => l.code.toLowerCase() === langCode.toLowerCase());

                if (language && fieldName) {
                    if (!newState.translations[langCode]) {
                        newState.translations[langCode] = {
                            id: null,
                            isActive: true,
                            languageId: language.id,
                            text: '',
                            description: '',
                            seoUrl: '',
                            metaKeywords: '',
                            metaDescription: '',
                        };
                    }

                    newState.translations[langCode][fieldName] = value;

                    if (prev.translations && prev.translations[langCode] && prev.translations[langCode].id) {
                        newState.translations[langCode].id = prev.translations[langCode].id;
                    }
                } else {
                    if (!language) {
                        console.warn(`Language with code ${langCode} not found in supportedLanguages. Path: ${resolvedName}`);
                    }
                }
            } else {
                const keys = resolvedName.split('.');
                let current = newState;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]] || typeof current[keys[i]] !== 'object') {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            }
            return newState;
        });
    }, [setFormData, supportedLanguages]);

    const handleDropdownChange = useCallback((fieldName, value) => {
        setFormData(prev => {
            const newState = { ...prev, [fieldName]: value };
            if (fieldName === 'mainCategory') {
                newState.secondaryCategory = null;
                newState.subCategory = null;
            } else if (fieldName === 'secondaryCategory') {
                newState.subCategory = null;
            }
            return newState;
        });
    }, []);

    const handleDeleteConfirm = async () => {
        if (!formData.id) {
            console.error("Silinecek kayıt ID'si bulunamadı.");
            return;
        }
        try {
            await deleteFaultCodesFn(formData.id).unwrap();
            hideDialog();
            if (onSuccessCallback) onSuccessCallback();
        } catch (err) {
            console.error("DEBUG (useManageCategoryDialog): Kayıt silme başarısız:", err);
        }
    };

    const handleSubmit = async () => {
        setSubmitted(true);

        const englishTranslation = formData.translations?.en;
        const turkishTranslation = formData.translations?.tr;
        let missingTranslations = [];
        if (!englishTranslation || !englishTranslation.text?.trim()) {
            missingTranslations.push("İngilizce");
        }
        if (!turkishTranslation || !turkishTranslation.text?.trim()) {
            missingTranslations.push("Türkçe");
        }
        if (missingTranslations.length > 0) {
            console.warn(`${missingTranslations.join(' ve ')} kategori adı (text) zorunludur.`);
            return;
        }

        const mainCatVal = formData.mainCategory;
        const secondCatVal = formData.secondaryCategory;
        const subCatVal = formData.subCategory;

        const mainCatLookupCode = typeof mainCatVal === 'object' && mainCatVal !== null ? mainCatVal.lookupCode : mainCatVal;
        const secondCatLookupCode = typeof secondCatVal === 'object' && secondCatVal !== null ? secondCatVal.lookupCode : secondCatVal;
        const subCatLookupCode = typeof subCatVal === 'object' && subCatVal !== null ? subCatVal.lookupCode : subCatVal;

        if (categoryLevel === CATEGORY_LEVELS.SECONDARY && !mainCatLookupCode) {
            console.warn("İkincil kategori için Ana Kategori seçimi zorunludur.");
            return;
        }
        if (categoryLevel === CATEGORY_LEVELS.SUB && (!mainCatLookupCode || !secondCatLookupCode)) {
            console.warn("Alt kategori için Ana ve İkincil Kategori seçimi zorunludur.");
            return;
        }
        if (categoryLevel === CATEGORY_LEVELS.SUB_PART && (!mainCatLookupCode || !secondCatLookupCode || !subCatLookupCode)) {
            console.warn("Alt parça kategori için Ana, İkincil ve Alt Kategori seçimi zorunludur.");
            return;
        }

        const translationsForApi = supportedLanguages
            .map(supportedLang => {
                const langCode = supportedLang.code.toLowerCase();
                const translationData = formData.translations?.[langCode];

                if (translationData && typeof translationData.text !== 'undefined' && translationData.languageId !== null) {
                    return {
                        id: translationData.id || null,
                        languageId: supportedLang.id,
                        text: translationData.text,
                    };
                }
                return null;
            })
            .filter(Boolean);

        const payload = {
            id: isEditMode ? formData.id : null,
            isActive: formData.isActive,
            mainCategory: mainCatLookupCode || null,
            secondaryCategory: secondCatLookupCode || null,
            subCategory: subCatLookupCode || null,
            translations: translationsForApi,
        };

        console.log("Gönderilecek Payload:", payload);

        try {
            let response;
            if (isEditMode) {
                payload.id = formData.id;
                if (!payload.id) {
                    console.error("Güncelleme için Kategori ID'si bulunamadı!");
                    return;
                }
                switch (categoryLevel) {
                    case CATEGORY_LEVELS.MAIN: response = await updateMainCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SECONDARY: response = await updateSecondCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SUB: response = await updateSubCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SUB_PART: response = await updateSubPartCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.FAILURE_MODES: response = await updateFailureModesFn(payload).unwrap(); break;

                    default:
                        console.error("Bilinmeyen kategori seviyesi (güncelleme):", categoryLevel);
                        throw new Error("Bilinmeyen kategori seviyesi.");
                }
            } else {
                delete payload.id;
                switch (categoryLevel) {
                    case CATEGORY_LEVELS.MAIN: response = await addMainCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SECONDARY: response = await addSecondCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SUB: response = await addSubCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.SUB_PART: response = await addSubPartCategoryFn(payload).unwrap(); break;
                    case CATEGORY_LEVELS.FAILURE_MODES: response = await addFailureModesFn(payload).unwrap(); break;
                    default:
                        console.error("Bilinmeyen kategori seviyesi (ekleme):", categoryLevel);
                        throw new Error("Bilinmeyen kategori seviyesi.");
                }
            }

            console.log("API Yanıtı:", response);
            hideDialog();
            if (onSuccessCallback) {
                onSuccessCallback(response);
            }

        } catch (err) {
            console.error(`DEBUG (useManageCategoryDialog): ${isEditMode ? 'Güncelleme' : 'Ekleme'} başarısız:`, err);
        }
    };

    const getCurrentMutationState = useCallback(() => {
        if (isEditMode) {
            switch (categoryLevel) {
                case CATEGORY_LEVELS.MAIN: return { isLoading: isUpdatingMain, error: updateMainError };
                case CATEGORY_LEVELS.SECONDARY: return { isLoading: isUpdatingSecond, error: updateSecondError };
                case CATEGORY_LEVELS.SUB: return { isLoading: isUpdatingSub, error: updateSubError };
                case CATEGORY_LEVELS.SUB_PART: return { isLoading: isUpdatingSubPart, error: updateSubPartError };
                case CATEGORY_LEVELS.FAILURE_MODES: return { isLoading: isUpdatingFailureMode, error: updateFailureModeError };

                default: return { isLoading: false, error: null };
            }
        } else {
            switch (categoryLevel) {
                case CATEGORY_LEVELS.MAIN: return { isLoading: isAddingMain, error: addMainError };
                case CATEGORY_LEVELS.SECONDARY: return { isLoading: isAddingSecond, error: addSecondError };
                case CATEGORY_LEVELS.SUB: return { isLoading: isAddingSub, error: addSubError };
                case CATEGORY_LEVELS.SUB_PART: return { isLoading: isAddingSubPart, error: addSubPartError };
                case CATEGORY_LEVELS.FAILURE_MODES: return { isLoading: isAddingFailureMode, error: addFailureModeError };
                default: return { isLoading: false, error: null };
            }
        }
    }, [
        isEditMode, categoryLevel,
        isAddingMain, addMainError, isUpdatingMain, updateMainError,
        isAddingSecond, addSecondError, isUpdatingSecond, updateSecondError,
        isAddingSub, addSubError, isUpdatingSub, updateSubError,
        isAddingSubPart, addSubPartError, isUpdatingSubPart, updateSubPartError,
        isAddingFailureMode, addFailureModeError, isUpdatingFailureMode, updateFailureModeError
    ]);

    const { isLoading: currentIsProcessing, error: currentMutationError } = getCurrentMutationState();

    return {
        dialogVisible,
        formData,
        setFormData,
        submitted,
        isProcessing: currentIsProcessing || isLoadingTranslations,
        mutationError: currentMutationError || (isTranslationsError ? translationsError : null),
        isEditMode,
        openDialog,
        hideDialog,
        handleInputChange,
        handleDropdownChange,
        handleSubmit,
        handleDeleteConfirm,
        isDeleting
    };
};